import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Form, Spinner, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { SearchService } from '../../Services/Axios/Api';
import './Search.css';

const SEARCH_LIMIT = 20;
const DEBOUNCE_MS = 300;

const Search = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryFromUrl = searchParams.get('q') || '';
    const typesFromUrl = searchParams.get('types') || '';

    const [inputValue, setInputValue] = useState(queryFromUrl);
    const [selectedTypes, setSelectedTypes] = useState(
        () => (typesFromUrl ? typesFromUrl.split(',').filter(Boolean) : [])
    );
    const [availableTypes, setAvailableTypes] = useState([]);
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const activeTypesKey = useMemo(
        () => [...selectedTypes].sort().join(','),
        [selectedTypes]
    );

    const updateUrl = useCallback((q, types) => {
        const params = new URLSearchParams();
        if (q.trim()) params.set('q', q.trim());
        if (types.length) params.set('types', types.join(','));
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    useEffect(() => {
        setInputValue(queryFromUrl);
        setSelectedTypes(typesFromUrl ? typesFromUrl.split(',').filter(Boolean) : []);
    }, [queryFromUrl, typesFromUrl]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const response = await SearchService.getTypes();
                if (response.isSuccess) {
                    setAvailableTypes(response.result || []);
                }
            } catch {
                setAvailableTypes([]);
            }
        };
        loadTypes();
    }, []);

    useEffect(() => {
        const trimmed = queryFromUrl.trim();
        if (!trimmed) {
            setResults(null);
            setError(null);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await SearchService.search(trimmed, {
                    types: selectedTypes.length ? selectedTypes : undefined,
                    limit: SEARCH_LIMIT,
                });
                if (cancelled) return;
                if (response.isSuccess) {
                    setResults(response.result);
                } else {
                    setResults(null);
                    setError(response.errorMessages?.[0] || t('Search.Error'));
                }
            } catch (err) {
                if (!cancelled) {
                    setResults(null);
                    setError(err.message || t('Search.Error'));
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [queryFromUrl, activeTypesKey, t, selectedTypes]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        updateUrl(trimmed, selectedTypes);
    };

    const toggleType = (entityType) => {
        const next = selectedTypes.includes(entityType)
            ? selectedTypes.filter((type) => type !== entityType)
            : [...selectedTypes, entityType];
        setSelectedTypes(next);
        if (queryFromUrl.trim()) {
            updateUrl(queryFromUrl, next);
        }
    };

    const clearFilters = () => {
        setSelectedTypes([]);
        if (queryFromUrl.trim()) {
            updateUrl(queryFromUrl, []);
        }
    };

    const visibleGroups = (results?.groups || []).filter((group) => group.count > 0);

    return (
        <Container className="search-page mt-5 mb-5">
            <h1 className="search-page-title mb-4">{t('Search.Title')}</h1>

            <Form onSubmit={handleSubmit} className="search-form mb-4">
                <div className="search-form-bar">
                    <FontAwesomeIcon icon={faSearch} className="search-form-icon" />
                    <Form.Control
                        type="search"
                        className="search-form-input"
                        placeholder={t('Search.Placeholder')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        aria-label={t('Search.Placeholder')}
                    />
                    <button type="submit" className="search-form-submit">
                        {t('Search.Submit')}
                    </button>
                </div>
            </Form>

            {availableTypes.length > 0 && (
                <div className="search-filters mb-4">
                    <span className="search-filters-label">{t('Search.FilterBy')}</span>
                    <button
                        type="button"
                        className={`search-filter-chip ${selectedTypes.length === 0 ? 'active' : ''}`}
                        onClick={clearFilters}
                    >
                        {t('Search.AllTypes')}
                    </button>
                    {availableTypes.map((type) => (
                        <button
                            key={type.entityType}
                            type="button"
                            className={`search-filter-chip ${selectedTypes.includes(type.entityType) ? 'active' : ''}`}
                            onClick={() => toggleType(type.entityType)}
                        >
                            {t(`Search.${type.label}`)}
                        </button>
                    ))}
                </div>
            )}

            {!queryFromUrl.trim() && (
                <p className="search-hint text-muted">{t('Search.EnterQuery')}</p>
            )}

            {queryFromUrl.trim() && isLoading && (
                <div className="search-loading text-center py-5">
                    <Spinner animation="border" role="status" />
                    <p className="mt-3 text-muted">{t('Search.Loading')}</p>
                </div>
            )}

            {error && !isLoading && (
                <div className="search-error alert alert-danger" role="alert">{error}</div>
            )}

            {queryFromUrl.trim() && !isLoading && !error && results && (
                <>
                    <p className="search-summary text-muted mb-4">
                        {results.totalCount > 0
                            ? t('Search.ResultsCount', { count: results.totalCount, query: results.query })
                            : t('Search.NoResults', { query: results.query })}
                    </p>

                    {visibleGroups.length === 0 ? (
                        <p className="text-muted">{t('Search.NoResults', { query: results.query })}</p>
                    ) : (
                        visibleGroups.map((group) => (
                            <section key={group.entityType} className="search-group mb-5">
                                <h2 className="search-group-title">
                                    {t(`Search.${group.label}`)}
                                    <Badge bg="secondary" className="ms-2 me-2">{group.count}</Badge>
                                </h2>
                                <ul className="search-results-list list-unstyled">
                                    {group.items.map((item) => (
                                        <li key={`${item.entityType}-${item.id}`} className="search-result-item">
                                            <Link to={group.entityType === 'campaigns' ? `/campaigns/${item.title}?id=${item.id}` : item.url || '#'} className="search-result-link">
                                                {item.imageUrl && (
                                                    <img
                                                        src={item.imageUrl}
                                                        alt=""
                                                        className="search-result-image"
                                                    />
                                                )}
                                                <div className="search-result-body">
                                                    <span className="search-result-title">{item.title}</span>
                                                    {item.subtitle && (
                                                        <span className="search-result-subtitle">{item.subtitle}</span>
                                                    )}
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))
                    )}
                </>
            )}
        </Container>
    );
};

export default Search;
